import type { VehicleDims } from "../dims.js";
import { stepPowertrain, newGearbox, torqueCurve } from "./powertrain.js";
import type { Tune } from "./tune.js";

/**
 * Showroom numbers from the same powertrain and tune the physics drives with (pure, fast, no
 * Rapier): 0-100 km/h by a straight-line integration (traction limited by the driven wheels'
 * share of the weight, aero drag, rolling resistance, shift cuts) and the peak power.
 */
export function zeroTo100(
  dims: Pick<VehicleDims, "massKg" | "wheelRadius" | "class">,
  t: Tune,
): number {
  const mass = dims.massKg + 80;
  const g = 9.81;
  const share = t.drive === "awd" ? 1 : t.drive === "fwd" ? 0.56 : 0.5;
  const grip = t.grip * mass * g * share * 1.04;
  let box = newGearbox(t);
  let v = 0;
  const dt = 1 / 120;
  for (let time = 0; time < 90; time += dt) {
    const out = stepPowertrain(t, box, v, 1, false, dims.wheelRadius, dt);
    box = out.box;
    const drive = Math.min(out.force, grip);
    const drag = 0.5 * 1.2 * t.cdA * v * v + t.rolling * mass * g;
    v += ((drive - drag) / mass) * dt;
    if (v * 3.6 >= 100) return Math.round(time * 10) / 10;
  }
  return Infinity;
}

/** Peak engine power (kW). */
export function peakPowerKw(t: Tune): number {
  let best = 0;
  for (let rpm = t.idleRpm; rpm <= t.redline; rpm += 50)
    best = Math.max(best, (t.torqueNm * torqueCurve(t, rpm) * rpm * Math.PI * 2) / 60 / 1000);
  return Math.round(best);
}
